// HamburgerDrawer — T1.14 Mobile-First.
//
// Rendert auf <md eine Top-Bar (Hamburger links · Logo Mitte · Avatar rechts)
// und einen Slide-Drawer mit Navigation. Auf md+ versteckt — Desktop-Header
// bleibt der bestehende.

import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

interface Props {
  email?: string;
}

// Sanierung 03.07.: Hauptnavigation = Cockpit (die 4 Tabs Geld/Operativ/
// Zahlen/Team SIND die Navigation). Alles andere sind Werkzeuge — sichtbar,
// aber raus aus der ersten Reihe. Weniger Reiter, ein Einstiegspunkt.
const NAV_ITEMS: { label: string; href: string }[] = [
  { label: "Cockpit", href: "/" },
];

const WERKZEUG_ITEMS: { label: string; href: string }[] = [
  { label: "Decision-Queue", href: "/decisions" },
  { label: "Steuer-Termine", href: "/steuern" },
  { label: "Burn-Rate", href: "/burn-rate" },
  { label: "InfraX Liquiditätsplanung", href: "/cockpit/infrax" },
  { label: "Cash manuell eintragen", href: "/admin/cash-eintrag" },
  { label: "Tooltime-Upload", href: "/admin/tooltime-upload" },
  { label: "Zugangsdaten & Seeds", href: "/admin/zugangsdaten" },
];

export default function HamburgerDrawer({ email }: Props) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Drawer schliesst auf Route-Wechsel automatisch.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Esc schliesst Drawer; verhindert Body-Scroll wenn offen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="md:hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button
          type="button"
          aria-label="Menü öffnen"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-slate-200 hover:bg-slate-800/60 active:bg-slate-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden
          >
            <line x1="4" y1="7" x2="20" y2="7" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="17" x2="20" y2="17" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-slate-100 tracking-tight flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-400" />
          </span>
          X-Holding
        </span>
        <span
          className="h-9 w-9 rounded-full bg-slate-800 text-slate-300 text-xs font-semibold inline-flex items-center justify-center"
          title={email ?? "Account"}
        >
          {email ? email[0]?.toUpperCase() : "?"}
        </span>
      </div>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-slate-900 border-r border-slate-800 flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="text-sm font-semibold text-slate-100">Navigation</span>
              <button
                type="button"
                aria-label="Menü schließen"
                onClick={() => setOpen(false)}
                className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                  aria-hidden
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </button>
            </header>

            <nav className="flex-1 overflow-y-auto py-2">
              <ul className="space-y-1 px-2">
                {NAV_ITEMS.map((item) => {
                  const active = location.pathname === item.href;
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        aria-current={active ? "page" : undefined}
                        className={`block min-h-11 px-3 py-3 rounded-lg text-sm font-medium ${
                          active
                            ? "bg-sky-500/15 text-sky-200"
                            : "text-slate-200 hover:bg-slate-800/60"
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {/* Sanierung 03.07.: Werkzeuge — zweite Reihe, bewusst dezent */}
              <div className="mt-4 px-2">
                <h3 className="px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Werkzeuge
                </h3>
                <ul className="space-y-1">
                  {WERKZEUG_ITEMS.map((item) => {
                    const active = location.pathname === item.href;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          aria-current={active ? "page" : undefined}
                          className={`block min-h-11 px-3 py-3 rounded-lg text-xs ${
                            active
                              ? "bg-sky-500/15 text-sky-200"
                              : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* T2.14a: Firmen-Drill-Down scharfgeschaltet */}
              <div className="mt-4 px-2">
                <h3 className="px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Firmen
                </h3>
                <ul className="space-y-1">
                  {(
                    // Design-Wurf 03.07.: Dots aus der zentralen Markenpalette
                    // (DrainX war hier faelschlich green statt lime).
                    [
                      { slug: "xholding", label: "X-Holding UG", dot: "bg-indigo-500" },
                      { slug: "drainx", label: "DrainX GmbH", dot: "bg-lime-400" },
                      { slug: "gundk", label: "G+K Sanitärtechnik", dot: "bg-blue-600" },
                      { slug: "infrax", label: "InfraX GmbH", dot: "bg-red-500" },
                    ] as const
                  ).map((firma) => {
                    const active = location.pathname === `/firmen/${firma.slug}`;
                    return (
                      <li key={firma.slug}>
                        <Link
                          to={`/firmen/${firma.slug}`}
                          aria-current={active ? "page" : undefined}
                          className={`flex items-center gap-2 min-h-11 px-3 py-3 rounded-lg text-sm font-medium ${
                            active
                              ? "bg-sky-500/15 text-sky-200"
                              : "text-slate-200 hover:bg-slate-800/60"
                          }`}
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${firma.dot}`}
                            aria-hidden
                          />
                          {firma.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </nav>

            <footer className="border-t border-slate-800 p-3">
              <div className="text-[11px] text-slate-500 mb-2 px-1">Account</div>
              <div className="text-xs text-slate-300 px-1 truncate" title={email}>
                {email ?? "—"}
              </div>
              <a
                href="/login"
                className="mt-2 block min-h-11 px-3 py-3 rounded-lg text-sm font-medium text-rose-300 hover:bg-rose-500/10 text-center"
              >
                Logout
              </a>
            </footer>
          </aside>
        </>
      )}
    </div>
  );
}
